import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { InstallMobileOutlined } from '../icons';
import { useCourse } from '../contexts/CourseContext';
import './LoginPage.css';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(true);
  const [isIphone, setIsIphone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const courseCtx = useCourse();


  useEffect(() => {
    if (location.state?.registerSuccess) {
      alert('Seu cadastro foi criado com sucesso, acesse nossa plataforma com seus dados.');
    }

    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIphone(userAgent.includes('iphone') || userAgent.includes('ipad'));
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (isLoading) return;
    setIsLoading(true);

    try {
      const response = await axios.post(apiUrl + 'login', {
        email,
        password,
      });

      // O funil guiado (comentado por enquanto, ver MyMocks.js) ainda usa uma
      // flag em localStorage. Se quem loga agora é uma conta diferente da que
      // deixou esse dado salvo nesse navegador, limpa antes — senão a nova
      // conta herda estado de outra. onboarding_completed não precisa mais
      // dessa limpeza: agora vem do backend, por conta (ver GET /dashboard).
      // O token em si não fica mais acessível ao JS (cookie httpOnly setado
      // pelo backend) — o userId vem no corpo da resposta só pra esse fim.
      const previousUserId = localStorage.getItem('userId');
      const newUserId = response.data.userId ? String(response.data.userId) : null;
      if (previousUserId && newUserId && previousUserId !== newUserId) {
        localStorage.removeItem('trialFlowCompleted');
      }
      if (newUserId) {
        localStorage.setItem('userId', newUserId);
      }
      // Front e backend ficam em domínios diferentes em produção — o JS não
      // consegue ler o cookie csrfToken direto (cookie é isolado por
      // domínio). Por isso o backend também devolve o valor aqui, e o
      // interceptor em src/index.js usa esse valor salvo pra montar o header
      // X-CSRF-Token nas próximas requisições.
      if (response.data.csrfToken) {
        localStorage.setItem('csrfToken', response.data.csrfToken);
      }
      // O CourseProvider busca os concursos do usuário só uma vez, no mount do
      // app — antes do login, quando ainda não há token, essa busca volta
      // vazia e nunca é refeita sozinha (login é navegação client-side, sem
      // reload). Sem isso, o dropdown de troca de concurso (CourseSwitcher)
      // fica preso vazio até o usuário dar um F5 manual.
      await courseCtx?.refreshCourses();
      navigate('/simulados');
    } catch (err) {
      setError("Erro de login: " + (err.response ? err.response.data.message : err.message));
      setIsLoading(false);
    }
  };

  const handleRegister = () => {
    navigate('/cadastro');
  };

  const handleInstallInstructions = () => {
    if (window.navigator.share) {
      window.navigator.share({
        title: 'Preparate',
        text: 'Adicione o Preparate à sua tela inicial.',
        url: window.location.href,
      }).catch(() => {
      });
    } else {
      alert('No iPhone, abra o site no Safari e toque em Compartilhar > Adicionar à Tela Inicial.');
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();

    if (email.trim() === "") {
      alert("Por favor, insira seu e-mail para redefinir a senha.");
      return;
    }

    try {
      await axios.post(apiUrl + 'forgot-password', {
        email,
      });
      alert("Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha.");
    } catch (err) {
      console.error("Erro ao solicitar redefinição de senha: " + (err.response ? err.response.data.message : err.message));
    }
  };

  return (
    <div className="login-container">
      <Helmet>
        <title>Preparate - Simulados e Questões para Concursos</title>
        <meta name="description" content="Acesse simulados completos e questões de concursos atualizadas! Estude de forma prática e testando seus conhecimentos com conteúdo organizado por áreas." />
        <link rel="canonical" href="https://user.preparatequestoes.com.br/" />
      </Helmet>
      <form className="login-card" onSubmit={handleLogin} id="login-form">
        <div className="logo">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Preparate Questões logo" />
          <div className="logo-name">
            <span>PREPARATE</span>
            <span>QUESTÕES</span>
          </div>
        </div>

        <h1>Bem-vindo ao Preparate Questões</h1>
        {error && <p className="error-text">{error}</p>}

        <div className="field">
          <label htmlFor="email">E-mail</label>
          <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
          </svg>
          <input
            id="email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail de acesso"
            required
          />
        </div>

        <div className="field">
          <label htmlFor="password">Senha</label>
          <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 018 0v3" />
          </svg>
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Sua senha"
            required
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>

        <label className="remember">
          <input
            type="checkbox"
            checked={keepLoggedIn}
            onChange={() => setKeepLoggedIn((prev) => !prev)}
          />
          Manter-me conectado
        </label>

        <button type="submit" className="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="spinner" aria-hidden="true" />
              Entrando...
            </>
          ) : (
            <>
              Entrar
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </>
          )}
        </button>

        <div className="links">
          <span>Esqueceu sua senha? </span>
          <button type="button" className="inline-link" onClick={resetPassword}>Redefina aqui</button>
          <br />
          <span>Não tem uma conta? </span>
          <button type="button" className="inline-link" onClick={handleRegister}>Crie uma agora</button>
        </div>

        <div className="socials">
          <a href="https://instagram.com/preparatequestoes" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" />
              <circle cx="12" cy="12" r="4.2" />
              <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
            </svg>
          </a>
          <a href="https://wa.me/555596507899?text=Olá%2C%20gostaria%20de%20mais%20informações" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.39c1.44.79 3.06 1.2 4.71 1.2h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.876 9.876 0 0012.05 2h-.01zm5.8 14.16c-.24.68-1.4 1.3-1.93 1.38-.49.08-1.11.11-1.79-.11-.41-.13-.94-.3-1.62-.6-2.85-1.23-4.71-4.1-4.85-4.29-.14-.19-1.16-1.54-1.16-2.94s.73-2.09.99-2.37c.26-.29.56-.36.75-.36.19 0 .38 0 .54.01.17.01.41-.07.64.49.24.58.81 2 .88 2.15.07.15.12.32.02.51-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.16.28.71 1.17 1.52 1.9 1.05.94 1.93 1.23 2.21 1.37.28.14.44.12.6-.07.16-.19.68-.79.87-1.07.19-.28.37-.23.62-.14.26.09 1.63.77 1.91.91.28.14.47.21.54.33.07.12.07.68-.17 1.36z" />
            </svg>
          </a>
        </div>

        {isIphone && (
          <div className="install-guide-container">
            <button type="button" onClick={handleInstallInstructions} className="btn-install-home">
              <InstallMobileOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
              Adicionar na tela inicial
            </button>
            <p className="install-guide-text">
              No iPhone, abra o Safari, toque em Compartilhar e escolha “Adicionar à Tela Inicial”.
            </p>
          </div>
        )}
      </form>

      <a className="whatsapp-btn" href="https://chat.whatsapp.com/LNeuw6jblVl2Bv171V0592?mode=gi_t" target="_blank" rel="noopener noreferrer">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.39c1.44.79 3.06 1.2 4.71 1.2h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.876 9.876 0 0012.05 2h-.01zm5.8 14.16c-.24.68-1.4 1.3-1.93 1.38-.49.08-1.11.11-1.79-.11-.41-.13-.94-.3-1.62-.6-2.85-1.23-4.71-4.1-4.85-4.29-.14-.19-1.16-1.54-1.16-2.94s.73-2.09.99-2.37c.26-.29.56-.36.75-.36.19 0 .38 0 .54.01.17.01.41-.07.64.49.24.58.81 2 .88 2.15.07.15.12.32.02.51-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.16.28.71 1.17 1.52 1.9 1.05.94 1.93 1.23 2.21 1.37.28.14.44.12.6-.07.16-.19.68-.79.87-1.07.19-.28.37-.23.62-.14.26.09 1.63.77 1.91.91.28.14.47.21.54.33.07.12.07.68-.17 1.36z" />
        </svg>
        <span>Entrar no grupo do WhatsApp</span>
      </a>
    </div>
  );
};

export default Login;